import { Attachment, AttachmentStatus, AttachmentType } from '@/utils/fetchers/attachment'
import clsx from 'clsx'
import { uniqueId } from 'lodash-es'
import { type HTMLAttributes, type Ref } from 'react'
import LinkAttachmentPicker from './LinkAttachmentPicker'
import styles from './MixedAttachmentPicker.module.css'
import PictureAttachmentPicker from './PictureAttachmentPicker'
import VideoAttachmentPicker from './VideoAttachmentPicker'

export interface MixedAttachmentPickerProps extends HTMLAttributes<HTMLDivElement> {
	/**
	 * @default false
	 */
	noPicture?: boolean

	/**
	 * @default false
	 */
	noVideo?: boolean
	/**
	 * @default false
	 */
	noLink?: boolean

	/**
	 * @default false
	 */
	isPicturePickerDisabled?: boolean

	/**
	 * @default false
	 */
	isVideoPickerDisabled?: boolean

	/**
	 * @default false
	 */
	isLinkPickerDisabled?: boolean

	onAdd?: (attachments: Attachment[]) => void
	ref?: Ref<HTMLDivElement>
}

export default function MixedAttachmentPicker({
	noPicture = false,
	noVideo = false,
	noLink = false,
	isPicturePickerDisabled = false,
	isVideoPickerDisabled = false,
	isLinkPickerDisabled = false,
	onAdd,
	ref,
	className,
	...props
}: MixedAttachmentPickerProps) {
	const handlePictureAttachmentPick = (files: File[]) => {
		const attachments = files.map(
			(file) =>
				({
					id: uniqueId(),
					type: AttachmentType.PICTURE,
					file: file,
					status: AttachmentStatus.WAIT_TO_UPLOAD,
				}) as Attachment
		)

		onAdd?.(attachments)
	}

	return (
		<div className={clsx(styles.root, className)} {...props} ref={ref}>
			{!noPicture && (
				<PictureAttachmentPicker
					onSelect={handlePictureAttachmentPick}
					disabled={isPicturePickerDisabled}
				/>
			)}
			{!noVideo && (
				<VideoAttachmentPicker
					onSelect={(files) => {
						onAdd?.(
							files.map((file) => ({
								id: uniqueId(),
								type: AttachmentType.VIDEO,
								file: file,
								status: AttachmentStatus.WAIT_TO_UPLOAD,
							}))
						)
					}}
					disabled={isVideoPickerDisabled}
				/>
			)}
			{!noLink && (
				<LinkAttachmentPicker
					disabled={isLinkPickerDisabled}
					onSelect={(linkInfo) => {
						onAdd?.([
							{
								id: uniqueId(),
								type: AttachmentType.LINK,
								linkInfo,
							},
						])
					}}
				/>
			)}
		</div>
	)
}
